import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image } from 'lucide-react';
import QRCode from 'react-qr-code';
import TicketShape from './ui/TicketShape';

interface TicketCardProps {
  ticket: {
    id: string;
    eventTitle: string;
    eventImage: string;
    date: string;
    time: string;
    venue: string;
    seat?: string;
    qrCode: string;
  };
}

const TicketCard: React.FC<TicketCardProps> = ({ ticket }) => {
  const { t } = useTranslation();
  const [showQRCode, setShowQRCode] = useState(false);

  return (
    <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="relative w-full h-[400px] sm:h-[500px] md:h-[600px] lg:h-[700px]">
        <TicketShape 
          width="100%" 
          height="100%" 
          fillColor="#f9bb10"
          className="shadow-2xl"
        >
        {/* Left section - Event image */}
        <div className="absolute left-0 top-0 w-1/2 h-full p-6 sm:p-8 md:p-10 flex items-center justify-center">
          <div className="relative w-full h-[250px] sm:h-[300px] md:h-[350px] lg:h-[400px] overflow-hidden rounded-lg shadow-lg">
            <img
              src={ticket.eventImage}
              alt={ticket.eventTitle}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Right section - Event info, QR Code and actions */}
        <div className="absolute right-0 top-0 w-1/2 h-full p-6 sm:p-8 md:p-10 flex flex-col justify-center items-center space-y-4 sm:space-y-6">
          {/* Event title */}
          <h3 className="font-bold text-xl sm:text-2xl md:text-3xl lg:text-4xl text-white text-center line-clamp-2 leading-tight">
            {ticket.eventTitle}
          </h3>
          
          {/* Event date */}
          <div className="text-white font-bold text-lg sm:text-xl md:text-2xl lg:text-3xl text-center">
            {ticket.date}
          </div>

          {/* QR Code or Barcode */}
          <div className="mb-4 sm:mb-6">
            {showQRCode ? (
              <div className="bg-white p-3 sm:p-4 rounded-lg shadow-lg">
                <QRCode value={ticket.qrCode} size={100} />
              </div>
            ) : (
              <div className="w-32 h-20 sm:w-36 sm:h-24 bg-white rounded-lg flex items-center justify-center shadow-lg">
                <div className="flex space-x-1">
                  {Array.from({ length: 20 }).map((_, i) => (
                    <div
                      key={i}
                      className="w-0.5 bg-black"
                      style={{ height: `${Math.random() * 25 + 20}px` }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex flex-col space-y-3 sm:space-y-4 w-full max-w-xs">
            <button
              onClick={() => setShowQRCode(false)}
              className={`w-full flex items-center justify-center space-x-2 py-3 sm:py-4 px-4 sm:px-6 rounded-lg transition-all duration-200 text-sm sm:text-base font-medium ${
                !showQRCode
                  ? 'bg-white text-yellow-600'
                  : 'bg-white/20 text-white'
              }`}
            >
              <Image size={16} />
              <span>{t('ticket.image')}</span>
            </button>
            
            <button
              onClick={() => setShowQRCode(true)}
              className={`w-full flex items-center justify-center space-x-2 py-3 sm:py-4 px-4 sm:px-6 rounded-lg transition-all duration-200 text-sm sm:text-base font-medium ${
                showQRCode
                  ? 'bg-white text-yellow-600'
                  : 'bg-white/20 text-white'
              }`}
            >
              <span>{t('ticket.qrCode')}</span>
            </button>
          </div>
        </div>
        </TicketShape>
      </div>
    </div>
  );
};

export default TicketCard;
